import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ConsultationService, sharedApiClient } from '../fetcher';
import {
  IdentityService,
  TeamService,
  UserAbility,
  useNotifications,
  BROWSER_PAGE_LABEL,
  medPromptBrowserTitle,
  isAbortError,
} from '@medprompt/shared';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';

const DELETABLE_STATUSES = [
  ConsultationStatusEnum.READY.value,
  ConsultationStatusEnum.INVALID.value,
  ConsultationStatusEnum.ABANDONED.value,
];

const useConsultationRecord = ({ consultationId, user }) => {
  const { addNotification } = useNotifications();
  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);

  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  const addNotificationRef = useRef(addNotification);
  const consultationIdRef = useRef(consultationId);
  useEffect(() => { addNotificationRef.current = addNotification; }, [addNotification]);
  useEffect(() => { consultationIdRef.current = consultationId; }, [consultationId]);

  useEffect(() => {
    document.title = medPromptBrowserTitle(BROWSER_PAGE_LABEL.consultationDetail);
  }, []);

  // A new consultation starts from a loading state so the previous one is never shown, or acted
  // on, under the new URL; a response for a consultation the user has left is discarded.
  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();

    setLoading(true);
    setConsultation(null);
    setError(null);

    const load = async () => {
      try {
        const data = await consultationService.fetchConsultationData(consultationId, { signal: controller.signal });
        if (isCancelled) return;
        setConsultation(data);
        setError(null);
      } catch (err) {
        if (isCancelled || isAbortError(err)) return;
        console.error('Error fetching consultation:', err);
        setError('Failed to load consultation');
        addNotificationRef.current('consultation-fetch-error', 'Failed to load consultation', { isError: true });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    load();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [consultationId, consultationService]);

  /** Refetch the open consultation in place (no loading state), e.g. once a recording completes. */
  const reloadConsultation = useCallback(async () => {
    const targetId = consultationId;
    try {
      const data = await consultationService.fetchConsultationData(targetId);
      if (targetId !== consultationIdRef.current) return;
      setConsultation(data);
      setError(null);
    } catch (err) {
      if (targetId !== consultationIdRef.current) return;
      console.error('Error fetching consultation:', err);
      setError('Failed to load consultation');
      addNotificationRef.current('consultation-fetch-error', 'Failed to load consultation', { isError: true });
    } finally {
      if (targetId === consultationIdRef.current) {
        setLoading(false);
      }
    }
  }, [consultationId, consultationService]);

  useEffect(() => {
    const identityManager = new IdentityService();
    const teamManager = new TeamService();

    identityManager.fetchActiveClinicUsers()
      .then((users) => {
        setActiveUsers(users || []);
      })
      .catch((fetchError) => {
        console.error('Failed to fetch active users:', fetchError);
        setActiveUsers([]);
      });

    teamManager.fetchTeams()
      .then((teamsData) => {
        setTeams(teamsData || []);
      })
      .catch((fetchError) => {
        console.error('Failed to fetch teams:', fetchError);
        setTeams([]);
      });
  }, []);

  // Accepts either the next consultation or an updater. The updater form is what patches applied
  // after an await must use: spreading a `consultation` captured before the request would revert
  // whatever arrived from the update stream while that request was in flight.
  const updateConsultation = useCallback((update) => {
    setConsultation(prevConsultation => (
      typeof update === 'function' ? update(prevConsultation) : update
    ));
  }, []);

  const clearLoadError = useCallback(() => setError(null), []);

  const hasConsultWrite = Boolean(user?.authorities?.includes(UserAbility.CONSULT_WRITE.key));
  const consultationStatus = consultation?.status;
  const isEditable = hasConsultWrite && consultationStatus === ConsultationStatusEnum.READY.value;
  const canDelete = hasConsultWrite && Boolean(consultation) && DELETABLE_STATUSES.includes(consultationStatus);
  const canFileAway = Boolean(consultation?.nhsId && consultation?.patientId);

  const isRegenerationEnabled = useCallback(() => (
    hasConsultWrite
      && consultationStatus === ConsultationStatusEnum.READY.value
      && consultationStatus !== ConsultationStatusEnum.FILED_AWAY.value
  ), [hasConsultWrite, consultationStatus]);

  const handleRegenerationClick = useCallback(() => {
    console.log("Consultation content regeneration triggered");
  }, []);

  const regenerateService = useCallback(async (entityId, contentType) => (
    consultationService.regenerateConsultationContent(entityId, contentType)
  ), [consultationService]);

  return {
    consultationService,
    consultation,
    consultationStatus,
    loading,
    error,
    reloadConsultation,
    updateConsultation,
    clearLoadError,
    activeUsers,
    teams,
    hasConsultWrite,
    isEditable,
    canDelete,
    canFileAway,
    isRegenerationEnabled,
    handleRegenerationClick,
    regenerateService,
  };
};

export default useConsultationRecord;
